import React, { useCallback } from 'react';
import styled from 'astroturf/react';
import UnionIcon from 'js/design_system/icons/union.svg';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';

const SignupBannerContainer = styled.div`
  margin-top: var(--spacing-xs-8, 8px);
  background: var(--brand-colors-expanded-colors-turquoise-300, #b9d5d7);
  border-radius: var(--corner-radius-radius-md, 8px);
  padding: var(--spacing-sm-24);
  position: relative;
  z-index: 10; /* This ensures the CTA is above the page element for click handling */

  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs-16);

  @include breakpoint(medium, large) {
    margin-top: var(--spacing-sm-24, 24px);
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: space-between;
    gap: var(--spacing-sm-24);
  }
`;

const SignupBannerIconWrapper = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  flex-shrink: 0;
  height: 48px;
  max-height: 48px;
  width: 48px;

  & svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @include breakpoint(medium, large) {
    height: 80px;
    max-height: 80px;
    width: 80px;
  }
`;

const SignupBannerContent = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: row;
  gap: var(--spacing-xs-12);
  align-items: center;

  @include breakpoint(medium, large) {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: var(--spacing-sm-24);
  }
`;

const SignupBannerMessage = styled(Heading)`
  @import 'stylesheets/design_system/breakpoint';
  display: inline-block;
  font-size: var(--type-sans-serif-xs-size, 18px) !important;
  line-height: var(--type-sans-serif-xs-line-height, 22px) !important;

  @include breakpoint(medium, large) {
    font-size: var(--type-sans-serif-sm-size, 24px) !important;
    line-height: var(--type-sans-serif-sm-line-height, 29px) !important;
  }
`;

const SignupBannerButton = styled(Button)`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
  border-radius: var(--corner-radius-radius-md, 8px);
  background: var(
    --brand-colors-expanded-colors-turquoise-950,
    #0f373a
  ) !important;
  border-color: var(
    --brand-colors-expanded-colors-turquoise-950,
    #0f373a
  ) !important;
  color: var(--brand-colors-expanded-colors-turquoise-300, #b9d5d7);

  &:not(:disabled):hover {
    background: #0a2529 !important;
    color: #d4e8e9 !important;
  }

  @include breakpoint(medium, large) {
    width: auto;
  }
`;

export const SignupBanner: React.FC = () => {
  const handleSignupClick = useCallback(
    (_e: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof window === 'undefined') return;

      document.dispatchEvent(
        new CustomEvent('open-login-modal', {
          detail: {
            postLoginRedirectUrl: window.location.href,
            clickTrackingContext: { source: 'loswp--signup-banner' },
            registrationParams: { user_source: 'download' },
          },
        }),
      );
    },
    [],
  );

  return (
    <SignupBannerContainer>
      <SignupBannerContent>
        <SignupBannerIconWrapper>
          <UnionIcon size={80} />
        </SignupBannerIconWrapper>
        <SignupBannerMessage>
          Download research papers for <strong>free!</strong>
        </SignupBannerMessage>
      </SignupBannerContent>
      <SignupBannerButton
        size="lg"
        onClick={handleSignupClick}
        iconRight="arrow_forward"
      >
        Join us!
      </SignupBannerButton>
    </SignupBannerContainer>
  );
};
